/**
 * Diálogo de exportación de un listado a CSV o a Excel. El espejo del de
 * importación, y a propósito mucho más corto: sacar datos no necesita tres
 * pasos, necesita **una** decisión informada.
 *
 * Es genérico como todo el renderer: no sabe qué modelo está exportando. Las
 * columnas, sus etiquetas y cuáles vienen tildadas las manda el backend a partir
 * del `ModelSpec`. Agregar un módulo no agrega una línea acá.
 *
 * Tres decisiones que conviene conocer antes de tocarlo:
 *
 * 1. **Se dice cuántas filas van a salir ANTES de descargar.** Al abrirse, el
 *    diálogo consulta `export/<modelo>/preview/` con los filtros que la pantalla
 *    tiene puestos y muestra el número. Sin eso, el usuario se entera del tope
 *    del servidor cuando abre el archivo y le faltan filas — y en un libro
 *    contable eso se descubre cuando ya se presentó.
 * 2. **«Lo filtrado» vs «todo» es una elección explícita.** El default es lo
 *    filtrado, porque es lo que la persona está mirando; «todo» es un segundo
 *    click, no un descuido. Cambiar la opción vuelve a contar: el número que se
 *    ve siempre corresponde al archivo que se va a bajar.
 * 3. **El filtro y el orden no se replican acá.** Se mandan tal como los tiene
 *    la pantalla y el backend arma el queryset con las mismas funciones que el
 *    listado. Cualquier lógica de filtrado escrita en este archivo sería una
 *    segunda verdad que se desincroniza con la primera.
 */

import { useEffect, useMemo, useRef, useState } from 'react';

import { Button } from '@/components/shell/primitives';
import { notify } from '@/components/shell/toast';
import { BotonBloqueado } from '@/views/MotivoBloqueo';
import { api } from '@/lib/api';
import type { Domain, ExportFormat, ExportPreview, ModelSpec } from '@/lib/types';

export interface ExportDialogProps {
  spec: ModelSpec;
  open: boolean;
  onClose: () => void;
  /** El dominio que la pantalla tiene puesto: búsqueda, filtros y archivados. */
  domain: Domain;
  /** El orden de la pantalla, en el mismo formato que `?order=`. */
  order?: string | undefined;
  /** `true` si la lista está mostrando también los archivados. */
  includeInactive?: boolean;
}

const FORMATOS: { valor: ExportFormat; titulo: string; detalle: string }[] = [
  {
    valor: 'xlsx',
    titulo: 'Excel (.xlsx)',
    detalle:
      'El que conviene para el contador: los importes son números, las fechas son fechas y un CUIT sigue siendo texto.',
  },
  {
    valor: 'csv',
    titulo: 'CSV (.csv)',
    detalle:
      'Texto plano, para importar en otro sistema. Excel decide los tipos al abrirlo: un código puede volverse fecha.',
  },
];

/**
 * Dispara la descarga con el nombre que trae el backend. Mismo helper que el
 * resto de las descargas: `<a download>` efímero y la object URL liberada un
 * tick después, porque algunos navegadores cancelan la descarga si se libera en
 * el mismo turno del click.
 */
function guardarArchivo(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const enlace = document.createElement('a');
  enlace.href = url;
  enlace.download = filename;
  document.body.appendChild(enlace);
  enlace.click();
  enlace.remove();
  window.setTimeout(() => {
    URL.revokeObjectURL(url);
  }, 0);
}

export function ExportDialog({
  spec,
  open,
  onClose,
  domain,
  order,
  includeInactive = false,
}: ExportDialogProps) {
  const panel = useRef<HTMLDivElement>(null);
  const focoPrevio = useRef<HTMLElement | null>(null);

  const [formato, setFormato] = useState<ExportFormat>('xlsx');
  const [soloFiltrado, setSoloFiltrado] = useState(true);
  const [columnas, setColumnas] = useState<string[]>([]);
  const [vista, setVista] = useState<ExportPreview | null>(null);
  const [contando, setContando] = useState(false);
  const [bajando, setBajando] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Cada apertura arranca de cero: heredar la selección de columnas de la
  // exportación anterior sobre otro listado es la forma más rápida de bajar un
  // archivo que no es el que se pidió.
  useEffect(() => {
    if (!open) return;
    setFormato('xlsx');
    setSoloFiltrado(true);
    setColumnas([]);
    setVista(null);
    setError(null);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    focoPrevio.current = document.activeElement as HTMLElement | null;

    const alTeclear = (evento: KeyboardEvent) => {
      if (evento.key === 'Escape') {
        evento.stopPropagation();
        onClose();
      }
    };
    document.addEventListener('keydown', alTeclear, true);
    const overflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const temporizador = window.setTimeout(() => {
      panel.current?.querySelector<HTMLElement>('[data-foco-inicial]')?.focus();
    }, 0);

    return () => {
      document.removeEventListener('keydown', alTeclear, true);
      document.body.style.overflow = overflow;
      window.clearTimeout(temporizador);
      focoPrevio.current?.focus();
    };
  }, [open, onClose]);

  const dominioEfectivo: Domain = soloFiltrado ? domain : [];

  // El conteo se rehace cuando cambia lo que va a salir. Es el compromiso del
  // diálogo: el número que se ve corresponde SIEMPRE al archivo que se baja.
  useEffect(() => {
    if (!open) return;
    const control = new AbortController();
    setContando(true);
    setError(null);
    api
      .exportPreview(
        spec.model,
        {
          // El formato va en el conteo aunque no cambie el número: de él sale el
          // `filename` que el pie muestra, y un pie que dice «.csv» mientras el
          // botón dice «Exportar a Excel» es una contradicción en pantalla.
          formato,
          domain: dominioEfectivo,
          ...(order === undefined ? {} : { order }),
          include_inactive: soloFiltrado && includeInactive,
        },
        control.signal,
      )
      .then((datos) => {
        setVista(datos);
        // Las columnas se fijan una sola vez, con las de la pantalla: si se
        // repusieran en cada conteo, tildar una columna y cambiar el alcance
        // borraría la elección del usuario sin avisar.
        setColumnas((actuales) =>
          actuales.length > 0 ? actuales : datos.fields.filter((c) => c.default).map((c) => c.name),
        );
      })
      .catch((excepcion: unknown) => {
        if (excepcion instanceof DOMException && excepcion.name === 'AbortError') return;
        setError(excepcion instanceof Error ? excepcion.message : 'No se pudo contar el listado.');
      })
      .finally(() => {
        setContando(false);
      });

    return () => {
      control.abort();
    };
    // `dominioEfectivo` es un array nuevo en cada render: se depende de su
    // serialización, que es lo que de verdad cambia el resultado.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, spec.model, JSON.stringify(dominioEfectivo), order, soloFiltrado, includeInactive, formato]);

  const campos = vista?.fields ?? [];
  const elegidas = useMemo(() => new Set(columnas), [columnas]);
  const totalAExportar = vista === null ? 0 : Math.min(vista.count, vista.max_rows);
  const seTrunca = vista !== null && vista.count > totalAExportar;

  const alternarColumna = (nombre: string): void => {
    setColumnas((actuales) =>
      actuales.includes(nombre) ? actuales.filter((c) => c !== nombre) : [...actuales, nombre],
    );
  };

  const exportar = async (): Promise<void> => {
    setBajando(true);
    setError(null);
    try {
      const { blob, filename, rowCount, truncated } = await api.exportRecords(spec.model, {
        formato,
        columns: columnas,
        domain: dominioEfectivo,
        ...(order === undefined ? {} : { order }),
        include_inactive: soloFiltrado && includeInactive,
      });
      guardarArchivo(blob, filename);
      if (truncated === true) {
        notify.warning(
          `Se exportaron ${rowCount ?? 0} de ${vista?.count ?? 0} filas`,
          'El archivo llegó al tope del sistema y lo dice en la última fila. Filtrá el listado para bajar el resto.',
        );
      } else {
        notify.success(
          `Se exportaron ${rowCount ?? 0} ${
            (rowCount ?? 0) === 1 ? spec.label.toLowerCase() : spec.label_plural.toLowerCase()
          }`,
        );
      }
      onClose();
    } catch (excepcion) {
      setError(excepcion instanceof Error ? excepcion.message : 'No se pudo generar el archivo.');
    } finally {
      setBajando(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center p-2 sm:items-center sm:p-4">
      <div className="absolute inset-0 bg-fg/30 animate-fade-in" onClick={onClose} aria-hidden="true" />
      <div
        ref={panel}
        role="dialog"
        aria-modal="true"
        aria-labelledby="exportar-titulo"
        className="relative flex max-h-[92vh] w-full max-w-2xl flex-col rounded-xl border border-line bg-surface shadow-lg animate-slide-up"
      >
        <header className="shrink-0 border-b border-line px-5 py-4">
          <h2 id="exportar-titulo" className="text-lg font-semibold">
            Exportar {spec.label_plural.toLowerCase()}
          </h2>
          <p className="mt-1 text-sm text-fg-muted" aria-live="polite">
            {contando
              ? 'Contando…'
              : vista === null
                ? '—'
                : `Se van a exportar ${totalAExportar.toLocaleString('es-AR')} ${
                    totalAExportar === 1
                      ? spec.label.toLowerCase()
                      : spec.label_plural.toLowerCase()
                  }`}
          </p>
        </header>

        <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">
          {error !== null && (
            <p
              role="alert"
              className="mb-4 rounded-lg border border-danger/30 bg-danger-soft px-3 py-2 text-sm text-danger"
            >
              {error}
            </p>
          )}

          {seTrunca && (
            <p
              role="status"
              className="mb-4 rounded-lg border border-warning/30 bg-warning-soft px-3 py-2 text-sm text-warning"
            >
              El listado tiene {(vista?.count ?? 0).toLocaleString('es-AR')} filas y el máximo por
              archivo es {(vista?.max_rows ?? 0).toLocaleString('es-AR')}. Se van a exportar las
              primeras {totalAExportar.toLocaleString('es-AR')} y el archivo lo va a decir en la
              última fila. Filtrá el listado para bajar el resto.
            </p>
          )}

          <fieldset className="mb-5">
            <legend className="mb-2 text-sm font-medium">Formato</legend>
            <div className="grid gap-2 sm:grid-cols-2">
              {FORMATOS.map((opcion, indice) => (
                <label
                  key={opcion.valor}
                  className={`flex cursor-pointer gap-2 rounded-lg border px-3 py-2 text-sm ${
                    formato === opcion.valor
                      ? 'border-accent/40 bg-accent-soft'
                      : 'border-line bg-surface-raised'
                  }`}
                >
                  <input
                    type="radio"
                    name="formato-exportacion"
                    className="mt-0.5 h-4 w-4 accent-[rgb(var(--accent))]"
                    checked={formato === opcion.valor}
                    onChange={() => {
                      setFormato(opcion.valor);
                    }}
                    {...(indice === 0 ? { 'data-foco-inicial': true } : {})}
                  />
                  <span className="min-w-0">
                    <span className="block font-medium">{opcion.titulo}</span>
                    <span className="block text-xs text-fg-muted">{opcion.detalle}</span>
                  </span>
                </label>
              ))}
            </div>
          </fieldset>

          <fieldset className="mb-5">
            <legend className="mb-2 text-sm font-medium">Qué filas</legend>
            <div className="grid gap-2 sm:grid-cols-2">
              <label
                className={`flex cursor-pointer gap-2 rounded-lg border px-3 py-2 text-sm ${
                  soloFiltrado ? 'border-accent/40 bg-accent-soft' : 'border-line bg-surface-raised'
                }`}
              >
                <input
                  type="radio"
                  name="alcance-exportacion"
                  className="mt-0.5 h-4 w-4 accent-[rgb(var(--accent))]"
                  checked={soloFiltrado}
                  onChange={() => {
                    setSoloFiltrado(true);
                  }}
                />
                <span className="min-w-0">
                  <span className="block font-medium">Lo que estoy viendo</span>
                  <span className="block text-xs text-fg-muted">
                    Con los filtros y el orden de la pantalla.
                  </span>
                </span>
              </label>
              <label
                className={`flex cursor-pointer gap-2 rounded-lg border px-3 py-2 text-sm ${
                  !soloFiltrado ? 'border-accent/40 bg-accent-soft' : 'border-line bg-surface-raised'
                }`}
              >
                <input
                  type="radio"
                  name="alcance-exportacion"
                  className="mt-0.5 h-4 w-4 accent-[rgb(var(--accent))]"
                  checked={!soloFiltrado}
                  onChange={() => {
                    setSoloFiltrado(false);
                  }}
                />
                <span className="min-w-0">
                  <span className="block font-medium">Todo el listado</span>
                  <span className="block text-xs text-fg-muted">
                    Sin filtros. Sigue siendo sólo lo que tenés permiso de ver.
                  </span>
                </span>
              </label>
            </div>
          </fieldset>

          <fieldset>
            <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
              <legend className="text-sm font-medium">
                Columnas{' '}
                <span className="font-normal text-fg-muted">
                  ({columnas.length} de {campos.length})
                </span>
              </legend>
              <div className="flex gap-2">
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => {
                    setColumnas(campos.map((c) => c.name));
                  }}
                >
                  Todas
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => {
                    setColumnas(campos.filter((c) => c.default).map((c) => c.name));
                  }}
                >
                  Las de la pantalla
                </Button>
              </div>
            </div>
            <div className="grid max-h-64 gap-1 overflow-y-auto rounded-lg border border-line bg-surface-raised p-2 sm:grid-cols-2">
              {campos.map((campo) => (
                <label
                  key={campo.name}
                  className="flex items-center gap-2 rounded px-2 py-1 text-sm hover:bg-app-subtle"
                >
                  <input
                    type="checkbox"
                    className="h-4 w-4 rounded border-line accent-[rgb(var(--accent))]"
                    checked={elegidas.has(campo.name)}
                    onChange={() => {
                      alternarColumna(campo.name);
                    }}
                  />
                  <span className="min-w-0 truncate">{campo.label}</span>
                </label>
              ))}
              {campos.length === 0 && !contando && (
                <p className="px-2 py-1 text-sm text-fg-muted">No hay columnas exportables.</p>
              )}
            </div>
          </fieldset>
        </div>

        <footer className="flex shrink-0 flex-col-reverse gap-2 border-t border-line px-5 py-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="text-xs text-fg-faint">{vista?.filename}</div>
          <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
            <Button variant="ghost" onClick={onClose}>
              Cancelar
            </Button>
            {(() => {
              const etiqueta = `Exportar ${formato === 'xlsx' ? 'a Excel' : 'a CSV'}`;
              /*
                La causa más común —destildó todas las columnas— es la más fácil
                de arreglar y era la que menos se explicaba: botón gris y nada
                más. Se nombra la casilla que hay que volver a marcar.
              */
              const motivo = contando
                ? 'Se está contando cuántos registros entran en la exportación. Un segundo.'
                : columnas.length === 0
                  ? 'No hay ninguna columna marcada, así que la planilla saldría vacía. Marcá al menos una en la lista de la izquierda.'
                  : totalAExportar === 0
                    ? 'Con el filtro que tenés puesto no hay ningún registro para exportar. Cambiá el filtro en la lista y volvé a abrir esta ventana.'
                    : null;
              return motivo === null ? (
                <Button
                  variant="primary"
                  loading={bajando}
                  onClick={() => {
                    void exportar();
                  }}
                >
                  {etiqueta}
                </Button>
              ) : (
                <BotonBloqueado variant="primary" label={etiqueta} motivo={motivo} />
              );
            })()}
          </div>
        </footer>
      </div>
    </div>
  );
}
